import { Component, Inject, OnDestroy, OnInit, ViewContainerRef } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { NextcloudFile, Widget, WidgetRoleFilter, WidgetSetting, WidgetUserFilter } from 'src/app/classes';
import { IWidgetProviderService, WIDGET_PROVIDER_SERVICE, WIDGET_STANDALONE_COMPONENT_SERVICE } from 'src/app/interfaces';
import { WidgetViewStandaloneComponentService } from '../widget-view-standalone/widget-view-standalone-component-service';
import { WidgetBarPosition, WidgetTypeMap } from '../widget-globals';
import { CustomChartView, WidgetComponent } from '../widget/widget.component';
import { FormControl } from '@angular/forms';
import { UserFilterDialogComponent } from '../user-filter-dialog/user-filter-dialog.component';
import { SelectFileDialogComponent, SelectFileDialogComponentInputData } from 'src/app/lib/nextcloud/components/dialog/select-file-dialog/select-file-dialog.component';
import { WidgetService } from 'src/app/services/widget.service';

@Component({
  selector: 'app-widget-menu',
  templateUrl: './widget-menu.component.html',
  styleUrls: ['./widget-menu.component.css']
})
export class WidgetMenuComponent implements OnDestroy, OnInit {

  typename: string = "Widget";

  refreshMinutesControl = new FormControl();
  iFrameControl = new FormControl();

  canChangeIcon: boolean = true;
  showUserFilterOption: boolean = false;
  showRoleFilterOption: boolean = false;
  toolbarPositions = [];
  userFilters: WidgetUserFilter[] = [];
  roleFilters: WidgetRoleFilter[] = [];
  chartViews: CustomChartView[] = [];

  hasChartViews: boolean = false;
  iFrameSupport: boolean = false;

  generalSettings = [{
    title: "Widget bewegen",
    action: () => {
      this._ref.close();
      this._service.toggleControlBar();
    },
    cssClass: ''
  }, {
    title: "Widget tauschen",
    action: () => {
      this.close();
      this.widgetService.widget$.subscribe((widget) => this._service.changeWidgetType(widget));
    },
    cssClass: ''
  }, {
    title: "Widget löschen",
    action: () => this.widgetService.widget$.pipe(switchMap((x: Widget) => this._service.deleteWidget(x))).subscribe(() => location.reload()),
    cssClass: 'danger'
  }];

  private _subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: WidgetMenuComponentInputData,
    @Inject(WIDGET_STANDALONE_COMPONENT_SERVICE) private _service: WidgetViewStandaloneComponentService,
    @Inject(WIDGET_PROVIDER_SERVICE) public widgetService: IWidgetProviderService,
    private _widgetService: WidgetService,
    private _ref: MatDialogRef<WidgetMenuComponent>,
    private _dialog: MatDialog,
    private _viewContainerRef: ViewContainerRef
  ) {
    if (data) {
      if (data.typename) this.typename = data.typename;
      if (data.userFilters) this.userFilters = data.userFilters;
      if (data.roleFilters) this.roleFilters = data.roleFilters;
      if (data.chartViews) this.chartViews = data.chartViews;
      if (data.showUserFilterOption) this.showUserFilterOption = data.showUserFilterOption;
      if (data.showRoleFilterOption) this.showRoleFilterOption = data.showRoleFilterOption;
    }
    this.toolbarPositions = [];
    for (let key in WidgetBarPosition) {
      switch (WidgetBarPosition[key]) {
        case WidgetBarPosition.None:
          this.toolbarPositions.push({ title: "Keine Toolbar", value: WidgetBarPosition.None });
          break;
        case WidgetBarPosition.Top:
          this.toolbarPositions.push({ title: "Toolbar oben", value: WidgetBarPosition.Top });
          break;
        case WidgetBarPosition.Right:
          this.toolbarPositions.push({ title: "Toolbar rechts", value: WidgetBarPosition.Right });
          break;
        case WidgetBarPosition.Bottom:
          this.toolbarPositions.push({ title: "Toolbar unten", value: WidgetBarPosition.Bottom });
          break;
        case WidgetBarPosition.Left:
          this.toolbarPositions.push({ title: "Toolbar links", value: WidgetBarPosition.Left });
          break;
      }
    }
  }

  addCustomChartView(){
    let dialog = this._dialog.open(SelectFileDialogComponent, {
      panelClass: 'no-padding-dialog-container',
      autoFocus: false,
      data: {
        visibleTypes: ['json'],
        showFolders: true,
        fileSelected: (file: NextcloudFile) => this.widgetService.widget$.pipe(switchMap((widget: Widget) => this._widgetService.createWidgetChartView(widget.widgetid, file.href).pipe(tap((widgetSetting: WidgetSetting) => {
          widget.widgetsetting.push(widgetSetting);
          this.widgetService.addChartView(widgetSetting);
          dialog.close();
        }))))
      } as SelectFileDialogComponentInputData
    });
  }
  
  addStandardChartView() {

  }

  addIFrame(){
    this.widgetService.widget$
      .pipe(switchMap((widget: Widget) => this._widgetService.createWidgetIFrame(widget.widgetid, this.iFrameControl.value)))
      .subscribe((widgetSetting: WidgetSetting) => {
        this.widgetService.addIFrameView(widgetSetting.widgetsettingvalue);
        this.iFrameControl.reset();
      });
  }

  addUserFilter = () => {
    this.data.widgetComponent.openWidgetUserFilterDialog();
    this.close();
  }

  changeExistingChartView = (view: CustomChartView) => { }

  close = () => this._ref.close();

  deleteChartView(chartView: CustomChartView) {
    this.widgetService.widget$
      .pipe(
        take(1),
        map((widget: Widget) => widget.widgetsetting.find(x => x.widgetsettingid === chartView.widgetsettingid))
      )
      .subscribe(((setting: WidgetSetting) => this.widgetService.deleteWidgetSetting(setting)));
  }

  deleteIFrame(url: string){
    this.widgetService.widget$
      .pipe(
        take(1),
        map((widget: Widget) => widget.widgetsetting.find(x => x.widgetsettingdesc === 'iframe-href' && x.widgetsettingvalue === url))
      )
      .subscribe(((setting: WidgetSetting) => this.widgetService.deleteWidgetSetting(setting)));
  }

  deleteUserFilter = (filter: WidgetUserFilter) => this.widgetService.deleteUserFilter(filter);

  do(action) {
    action();
  }

  editUserFilter = (filter: WidgetUserFilter) => {
    this.data.widgetComponent.openWidgetUserFilterDialog(filter.filterid);
    this.close();
  }

  ngOnDestroy(): void {
    for (let sub of this._subscriptions) sub.unsubscribe();
    this._subscriptions = [];
  }

  ngOnInit(): void {
    switch (this.data.type) {
      case WidgetTypeMap.ChartWidget.code:
        this.hasChartViews = true;
        this.iFrameSupport = false;
        break;
      case WidgetTypeMap.IFrameWidget.code:
        this.hasChartViews = false;
        this.iFrameSupport = true;
        break;
      default:
        this.iFrameSupport = false;
        this.hasChartViews = false;
        break;
    }
    this._subscriptions.push(...[
      this.widgetService.widget$.subscribe((widget: Widget) => {
        let setting = widget.widgetsetting.find(x => x.widgetsettingdesc === 'refresh-minutes');
        if (setting && typeof setting.widgetsettingvalue === 'string') this.refreshMinutesControl.setValue(parseInt(setting.widgetsettingvalue));
      }),
      this.refreshMinutesControl
        .valueChanges
        .pipe(distinctUntilChanged(), debounceTime(50), switchMap((value: string) => this.widgetService
          .setSetting({ widgetsettingdesc: 'refresh-minutes', widgetsettingvalue: value } as WidgetSetting)))
        .subscribe((setting: WidgetSetting) => {
          console.log(setting);
        })
    ]);
  }

  openUserFilterDialog(identifier: number = null) {
    this._dialog.open(UserFilterDialogComponent, {
      panelClass: "no-padding-dialog-container",
      autoFocus: false,
      data: identifier,
      viewContainerRef: this._viewContainerRef
    });
    this.close();
  }

  setToolbarPosition(value: number) {
    this.widgetService
      .setSetting({ widgetsettingdesc: 'toolbar-position', widgetsettingvalue: value.toString() } as WidgetSetting)
      .subscribe(() => this.widgetService.widgetBarPosition = value);
  }

}

export class WidgetMenuComponentInputData {
  typename: string;
  type: number;
  toolbarChangeSubject = new Subject();
  widgetSettingDeleteSubject = new Subject();
  userFilterDeleteSubject = new Subject();
  userFilters: WidgetUserFilter[];
  roleFilters: WidgetRoleFilter[];
  chartViews: CustomChartView[];
  showUserFilterOption: boolean;
  showRoleFilterOption: boolean;
  widgetComponent: WidgetComponent;
}
